export const LUN_SOURCE = 'lun_source';

export const LAYERS = {
  points: 'residential_complex_points',
  contours_fill: 'residential_complex_polygons_fill',
  contours_line: 'residential_complex_polygons_line',
  sections: 'buildings_3d',
  cottage_areas_fill: 'lun_cottage_areas_fill',
  cottage_areas_line: 'lun_cottage_areas_line',
  residential_complex_names: 'residential_complex_names',
  infrastructure_contours_buildings: 'lun_infrastructure_contours_buildings',
  infrastructure_objects: 'lun_infrastructure_objects',
};

export const LAYERS_TO_COLORIZE = [
  LAYERS.points,
  LAYERS.contours_fill,
  LAYERS.sections,
  LAYERS.cottage_areas_fill,
  LAYERS.cottage_areas_line,
];
const COTTAGE_LAYERS = [LAYERS.cottage_areas_fill, LAYERS.cottage_areas_line];
const AREA_LAYERS = [LAYERS.contours_fill, LAYERS.contours_line, ...COTTAGE_LAYERS];

export const STATUS = {
  AVAILABLE: 1,
  WARNING: 2,
  FUTURE_AVAILABLE: 3,
};

export const PALETTE = {
  primary: '#F06400',
  light: '#F7B180',
  muted: '#B0B0B0',
  warning: '#CE3220',
  selected: '#000000',
  selectedFill: '#19195F',
  white: '#FFFFFF',
};

export const buildingIdMapFilter = ['get', 'lun_building_id'];
const sectionIdMapFilter = ['get', 'lun_section_id'];

export const getColor = (buildingId, options = {}) => {
  const {
    filtered,
    seenIds,
    layerId,
    selectedSectionIds,
    isFillLayer = false,
    withMutedAreas = false,
  } = options;

  const expression = [];
  // PALLETE.muted for muted points, contours
  // PALLETE.white for muted sections
  const isSectionsLayer = layerId === LAYERS.sections;
  const isCottageLayer = COTTAGE_LAYERS.includes(layerId);
  const mutedColor = isSectionsLayer ? PALETTE.white : PALETTE.muted;
  const selectedColor = isFillLayer ? PALETTE.selectedFill : PALETTE.selected;

  const selected = !isSectionsLayer && !isCottageLayer ? buildingId : null;

  if (selected) {
    if (Array.isArray(selected)) {
      expression.push(['in', buildingIdMapFilter, ['literal', selected]], selectedColor);
    } else {
      expression.push(['==', buildingIdMapFilter, selected], selectedColor);
    }
  }

  if (seenIds && layerId === LAYERS.points) {
    expression.push(['in', buildingIdMapFilter, ['literal', seenIds]], PALETTE.white);
  }

  if (selectedSectionIds) {
    expression.push(['in', sectionIdMapFilter, ['literal', selectedSectionIds]], selectedColor);
  }

  if (filtered) {
    const filteredIds = filtered.map((i) => i.building_id);
    const withFilteredIds = filteredIds.length > 0;

    if (withMutedAreas) {
      if (withFilteredIds) {
        expression.push(['!', ['in', buildingIdMapFilter, ['literal', filteredIds]]], mutedColor);
      } else {
        // if no building matches filters
        expression.push(true, mutedColor);
      }
    }

    if (isCottageLayer && withFilteredIds) {
      expression.push(['!', ['in', buildingIdMapFilter, ['literal', filteredIds]]], PALETTE.white);
    }

    if (AREA_LAYERS.includes(layerId)) {
      const buildingIdsByStatus = filtered.reduce((acc, b) => {
        if (!acc[b.status]) acc[b.status] = [];
        acc[b.status].push(b.building_id);
        return acc;
      }, {});

      if (buildingIdsByStatus[STATUS.WARNING]?.length) {
        expression.push(
          ['in', buildingIdMapFilter, ['literal', buildingIdsByStatus[STATUS.WARNING]]],
          PALETTE.warning,
        );
      }
    }

    if (isSectionsLayer) {
      const sectionsByStatus = filtered
        .flatMap((b) => b.sections || [])
        .reduce((acc, s) => {
          if (!acc[s.status]) acc[s.status] = [];
          acc[s.status].push(s.section_id);
          return acc;
        }, {});
      const warningSections = sectionsByStatus[STATUS.WARNING];
      if (warningSections?.length) {
        expression.push(['in', sectionIdMapFilter, ['literal', warningSections]], PALETTE.warning);
      }
      const futureSections = sectionsByStatus[STATUS.FUTURE_AVAILABLE];
      if (futureSections?.length) {
        expression.push(['in', sectionIdMapFilter, ['literal', futureSections]], PALETTE.light);
      }
      const availableSections = sectionsByStatus[STATUS.AVAILABLE];
      if (availableSections?.length) {
        expression.push(
          ['in', sectionIdMapFilter, ['literal', availableSections]],
          PALETTE.primary,
        );
      }
    }
  }

  const defaultColor = isSectionsLayer ? PALETTE.white : PALETTE.primary;

  if (expression.length === 0) {
    return defaultColor;
  }

  return ['case', ...expression, defaultColor];
};

export const getPointStrokeColor = ({ selected, seenIds, filteredIds }) => {
  const expression = [];

  if (selected) {
    expression.push(['==', buildingIdMapFilter, selected], PALETTE.white);
  }

  const mutedSeenIntersection = seenIds.filter((id) => !filteredIds.includes(id));

  expression.push(['in', buildingIdMapFilter, ['literal', mutedSeenIntersection]], PALETTE.muted);
  expression.push(['in', buildingIdMapFilter, ['literal', seenIds]], PALETTE.primary);
  expression.push(PALETTE.white);

  return ['case', ...expression];
};

export const getPointStrokeWidth = ({ selected, seenIds }) => {
  const expression = [];

  if (selected) {
    expression.push(['==', buildingIdMapFilter, selected], 1);
  }
  expression.push(['in', buildingIdMapFilter, ['literal', seenIds]], 1.5);
  expression.push(1);

  return ['case', ...expression];
};

export const initMapLayers = (mapInstance) => {
  if (!mapInstance) return;
  mapInstance.setLayoutProperty(LAYERS.contours_line, 'visibility', 'none');
  mapInstance.setLayoutProperty(LAYERS.infrastructure_contours_buildings, 'visibility', 'visible');
  mapInstance.setLayoutProperty(LAYERS.infrastructure_objects, 'visibility', 'visible');
};
